import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs';
import { AlertComponent } from 'src/app/modules/shared/components/alert/alert.component';
import { SpinnerComponent } from 'src/app/modules/shared/components/spinner/spinner.component';
import { Role, RoleRequest } from 'src/app/modules/user-profile/models/role-request.models';
import { RoleRequestService } from 'src/app/modules/user-profile/services/role-request.service';
import { MultiOrgFilterTAComponent } from '../multi-org-filter-ta/multi-org-filter-ta.component';
import { PaginatorModule } from 'eas-ui-lib/paginator';
import { zip } from 'rxjs';

@Component({
  selector: 'app-role-request-employee-overview',
  imports: [MultiOrgFilterTAComponent, FormsModule, NgSelectModule, CommonModule, RouterModule,
     AlertComponent, NgxSpinnerModule, SpinnerComponent, PaginatorModule],
  templateUrl: './role-request-employee-overview.component.html',
  styleUrl: './role-request-employee-overview.component.css'
})
export class RoleRequestEmployeeOverviewComponent implements OnInit, OnDestroy {
  public roleRequestService = inject(RoleRequestService);
  private spinner = inject(NgxSpinnerService);

  selectedType: string;
  selectedOrgId;
  employees$: Observable<any[]>;
  search$ = new Subject<string>();
  loading = false;
  employeesList = []
  selectedEmployeeKey;
  userRoles = []
  systemsForSelectedRole: any[] = [];
  dropdowns = {
    userRoles: []
  };
  selectedRole;
  isDewOrg = false;
  isDewStaff = false;
  hasProfileAdmin = false;
  unsubscribe$ = new Subject();

  constructor() {
    this.employees$ = this.search$.pipe(debounceTime(300), distinctUntilChanged(), switchMap(term => this.search(term)));

    this.roleRequestService.multiOrgFilters$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: any) => {
      this.isDewOrg = res?.selectedRoleGroup?.roleAssignGroupKey == '1';
      this.hasProfileAdmin = res.hasProfileAdmin;
      this.isDewStaff = false;

      this.getApplicationRoles(res)
      this.onDewStaffChange();
    });
  }

  async getApplicationRoles(multiOrgFilters) {
    if(!multiOrgFilters?.selectedRoleGroup?.roleAssignGroupKey) {
      this.dropdowns.userRoles = [];
      return;
    }
    this.spinner.show('employee-overview');
    this.roleRequestService.getRolesByUsageTypeAndAssignGroup(multiOrgFilters.selectedRoleGroup.roleAssignGroupKey)
    .pipe(takeUntil(this.unsubscribe$)).subscribe((orgRoles:Role[])  => {  
      const uniqSet = new Set();
      this.dropdowns.userRoles = orgRoles.filter(role => {
        const roleName = role.roleName;
        if (!uniqSet.has(roleName)) {
          uniqSet.add(roleName);
          return true;
        }
        return false;
      }).sort((a, b) => {
        if (a.roleName < b.roleName) {
          return -1;
        }
        if (a.roleName > b.roleName) {
          return 1;
        }
        return 0;
      });

      this.dropdowns.userRoles = this.dropdowns.userRoles.map(x => {
        x['applicationRoleName'] = x.roleName;
        x['applicationRoleDescript'] = x.roleDescript;
        x['effectiveDate'] = new Date();
        return x;
      });
      this.spinner.hide('employee-overview');
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
  }

  selectEmployee(employee) {
    this.selectedType = 'role';
    this.selectedEmployeeKey = {
      firstName: employee.person.firstName,
      lastName: employee.person.lastName,
      key: employee.person.key
    }
    this.onEmployeeSelect({ key: employee.person.key });
  }

  search(selectedEmployee) {
    this.loading = true;
    return this.roleRequestService.searchEmployee(selectedEmployee, this.isDewStaff, this.isDewOrg)
      .pipe(takeUntil(this.unsubscribe$), tap(() => this.loading = false));
  }

  onDewStaffChange() {
    this.employeesList = [];
    this.userRoles = [];
    this.selectedEmployeeKey = null;
    this.roleRequestsData.currentData = [];
    this.selectedRole = null;
    this.systemsForSelectedRole = [];
    this.pendingRequestsData.currentData = [];
  }

  onEmployeeSelect(event) {
    if (!event?.key) {
      this.employeesList = [];
      this.userRoles = [];
      this.selectedEmployeeKey = null;
      this.roleRequestsData.currentData = [];
      this.selectedRole = null;
      this.systemsForSelectedRole = [];
      this.pendingRequestsData.currentData = [];
      return;
    };
    this.spinner.show('employee-overview');
    zip(
      this.roleRequestService.getRolesByPersonKey(event.key),
      this.roleRequestService.getRoleRequestsByPersonKey(this.selectedEmployeeKey.key)
    ).subscribe(([rolesData, requestsData]) => {
      this.userRoles = rolesData.personOrgRole.personOrgRoleList.map(x => {
        x.roleName = x.role.name;
        x.roleKey = x.role.key;
        return x;
      }).filter(x => x.organization.key === this.roleRequestService.multiOrgFilters.selectedOrganization?.orgKey);
      this.userRoles.sort((a, b) => a.roleName?.localeCompare(b.roleName));

      const pendingRequestStates = [
        "create", "submit",
        "wait", "pending"
      ];

      requestsData.sort((a, b) => {
        return new Date(b.updateTs).getTime() - new Date(a.updateTs).getTime();
      });

      requestsData.forEach(x => {
        if (x.roleRequestTypeKey == 1) {
          x.roleRequestType = 'Add New Role';
        } else if (x.roleRequestTypeKey == 2) {
          x.roleRequestType = 'Remove Role';
        }
      });

      this.roleRequestsData.roleRequests = requestsData.filter((x: RoleRequest) => !pendingRequestStates.some(state => x.workflowStateName?.toLowerCase()?.includes(state)));
      this.onRoleRequestPageChanged(this.roleRequestsData.currentPage);
      this.pendingRequestsData.pendingRequests = requestsData.filter((x: RoleRequest) => pendingRequestStates.some(state => x.workflowStateName?.toLowerCase()?.includes(state)));
      this.onPendingRequestsPageChanged(this.pendingRequestsData.currentPage);

      this.spinner.hide('employee-overview');

    });
  }

  roleRequestsData = {
    roleRequests: [],
    currentPage: 1,
    pageSize: 5,
    currentData: [],
  }
  pendingRequestsData = {
    pendingRequests: [],
    currentPage: 1,
    pageSize: 5,
    currentData: [],
  }

  addSpacesAtCapitals(str) {
    return str.replace(/([A-Z])/g, ' $1').trim();
  }

  getApplicationRolesByRoleKey(roleKey: string) {
    let uniqSet = new Set();
    this.spinner.show('employee-overview');
    this.roleRequestService.getApplicationRolesByRoleKey(roleKey).subscribe(roles => {
      this.spinner.hide('employee-overview');
      this.systemsForSelectedRole = roles.map(x => {
        if (!uniqSet.has(x.roleKey)) {
          uniqSet.add(x.roleKey); return x
        }
        return null;
      }
      ).filter(x => x)
    })
  }

  onRoleSelect(roleKey) {
    if (roleKey){
      this.getPersonsByRoleKey(roleKey)
    }
  }

  getPersonsByRoleKey(key) {
    this.spinner.show('employee-overview');
    this.roleRequestService.getPersonsByRoleKey(key, this.roleRequestService.multiOrgFilters.selectedOrganization?.orgKey).subscribe(data => {
      this.spinner.hide('employee-overview');
      this.employeesList = data.personOrgRoleList;
    });
  }

  onPendingRequestsPageChanged(page: number) {
    this.pendingRequestsData.currentData = this.pendingRequestsData.pendingRequests.slice((page - 1) * this.pendingRequestsData.pageSize , page * this.pendingRequestsData.pageSize);
    this.pendingRequestsData.currentPage = page;
  }

  onRoleRequestPageChanged(page: number) {
    this.roleRequestsData.currentData = this.roleRequestsData.roleRequests.slice((page - 1) * this.roleRequestsData.pageSize , page * this.roleRequestsData.pageSize);
    this.roleRequestsData.currentPage = page;
  }

}
